import React, { useEffect, useState, useCallback, useRef } from "react"
import MetaTags from "react-meta-tags"
import { useDispatch, useSelector } from "react-redux"
import { Card, CardBody, Col, Container, Row, Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup } from "reactstrap"
import Datatable from "components/Datatables/datatable"
import Select from "react-select"
import { clientOptions } from "store/actions"
import debounce from "lodash/debounce"
import { get, post } from "helpers/api_helper"
import { ORDERBYNUM } from "helpers/url_helper"
import { runBatchLoader } from "utils/batchLoader"

const isType = "Warranty Card"
const styleSheet = {
  fontStyle: {
    fontWeight: "bold",
    background: "#424141",
    padding: "0px 15px",
    color: "#fff",
  },
}

const WarrantyCard = () => {
  const dispatch = useDispatch()
  const { DDOptions } = useSelector(state => state.Master)
  const [clientId, setClientId] = useState(null)
  const [challans, setChallans] = useState(null)
  const [listLoading, setListLoading] = useState(false)
  const [orderLoading, setOrderLoading] = useState(false)
  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
    search: "",
  })
  const [batchProgress, setBatchProgress] = useState(null)
  const batchCancelRef = useRef(false)
  const isBatchRunningRef = useRef(false)
  const totalRowsRef = useRef(0)

  useEffect(() => {
    if (challans?.total_rows) {
      totalRowsRef.current = challans.total_rows
    }
  }, [challans])

  const handleCancelBatch = () => {
    batchCancelRef.current = true
    isBatchRunningRef.current = false
    setBatchProgress(prev => prev ? ({ ...prev, active: false, stopped: true }) : null)
    setListLoading(false)
  }

  const handlePaginationChange = useCallback((newPag) => {
    setPagination(prev => {
      if (
        prev.page === newPag.page &&
        prev.limit === newPag.limit &&
        prev.search === newPag.search &&
        Boolean(prev.isAll) === Boolean(newPag.isAll)
      ) {
        return prev
      }
      return { ...prev, ...newPag }
    })
  }, [])

  const [selectedOrder, setSelectedOrder] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeProduct, setActiveProduct] = useState(null)
  const [warrantyYears, setWarrantyYears] = useState("")
  const [startDate, setStartDate] = useState(new Date().toISOString().substring(0, 10))

  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [previewCardData, setPreviewCardData] = useState(null)

  const calculateEndDate = (start, years) => {
    if (!start || !years) return "";
    const date = new Date(start);
    date.setFullYear(date.getFullYear() + parseInt(years));
    return date.toISOString().substring(0, 10);
  };

  const handleProductClick = (product) => {
    setActiveProduct(product)
    setWarrantyYears(product.warranty_years || "")
    setStartDate(new Date().toISOString().substring(0, 10))
    setIsModalOpen(true)
  }

  const handleSaveWarranty = async () => {
    if (!warrantyYears) {
      alert("Please enter warranty years")
      return;
    }
    const payload = {
      order_number: selectedOrder.order_number,
      product_id: activeProduct.product_id,
      product_type: activeProduct.product_type,
      units: activeProduct.unit,
      start_date: startDate,
      years: warrantyYears
    }

    const res = await post('/api/orders/saveWarrantyCard', payload)
    if (res.status === 200) {
      alert(`Warranty Card generated successfully!\nCode: ${res.response?.warrenty_code}`)
      setIsModalOpen(false)
      getData()
    } else {
      alert("Failed to save warranty card")
    }
  }

  const getData = async () => {
    const isAllSelected = pagination?.isAll || pagination?.limit === 'all' || (typeof pagination?.limit === 'number' && pagination?.limit > 100)
    const clientQuery = (clientId && clientId.value) ? `&client=${clientId.value}` : ''

    if (isAllSelected) {
      batchCancelRef.current = true
      await new Promise(r => setTimeout(r, 60))
      batchCancelRef.current = false
      isBatchRunningRef.current = true

      setListLoading(true)
      try {
        await runBatchLoader({
          fetchPage: async (page, batchSize) => {
            const url = `/api/orders/warrantyCards?page=${page}&limit=${batchSize}&search=${pagination?.search || ''}${clientQuery}`
            const res = await get(url)
            return res?.response || res || {}
          },
          batchSize: 250,
          initialTotal: totalRowsRef.current || 0,
          onProgress: setBatchProgress,
          onRowsLoaded: (accumulatedRows, totalCount) => {
            setChallans({
              total_rows: totalCount,
              rows: accumulatedRows
            })
          },
          cancelRef: batchCancelRef,
          runningRef: isBatchRunningRef,
        })
      } catch (err) {
        console.error("Error batch loading warranty cards:", err)
      } finally {
        setListLoading(false)
      }
    } else {
      setListLoading(true)
      setBatchProgress(null)
      try {
        let url = `/api/orders/warrantyCards?page=${pagination?.page}&limit=${pagination?.limit}&search=${pagination?.search}${clientQuery}`
        const res = await get(url)
        if (res && res.status == 200 && res.response) {
          setChallans(res.response)
        }
      } catch (err) {
        console.error("Error loading warranty cards:", err)
      } finally {
        setListLoading(false)
      }
    }
  }

  const debouncedGetOrdersByNumber = useCallback(
    debounce(value => {
      getOrdersByNumber(value)
    }, 600),
    []
  )

  const debouncedSearch = useCallback(
    debounce(value => {
      setPagination(prev => ({ ...prev, search: value, page: 1 }))
    }, 600),
    []
  )

  useEffect(() => {
    getData()
  }, [pagination, clientId])

  const parseTeeth = (teethJson) => {
    try {
      if (!teethJson) return "";
      const parsed = typeof teethJson === 'string' ? JSON.parse(teethJson) : teethJson;
      if (Array.isArray(parsed)) {
        return parsed.join(", ");
      } else if (parsed && typeof parsed === 'object') {
        return Object.values(parsed).flat().join(", ");
      }
      return teethJson;
    } catch (e) {
      return teethJson;
    }
  };

  const handlePreviewClick = (row) => {
    setPreviewCardData(row)
    setIsPreviewOpen(true)
  }

  // table Column Data
  let tableColumns = [
    {
      dataField: "sr_no",
      text: "#",
      sort: false,
    },
    {
      dataField: "warrenty_code",
      text: "Warranty Code",
      sort: true,
      formatter: (cell, row) => (
        <button
          type="button"
          className="btn btn-link p-0 text-start animate-hover"
          onClick={() => handlePreviewClick(row)}
          style={{ ...styleSheet.fontStyle, textDecoration: 'none' }}
        >
          {row.warrenty_code}
        </button>
      ),
    },
    {
      dataField: "verification_code",
      text: "Verification Code",
      sort: true,
    },
    {
      dataField: "frame_bar_code",
      text: "Order Number",
      sort: true,
    },
    {
      dataField: "case_number",
      text: "Case No",
      sort: true,
    },
    {
      dataField: "clientname",
      text: "Client",
      sort: true,
    },
    {
      dataField: "patiantname",
      text: "Patient",
      sort: true,
    },
    {
      dataField: "date",
      text: "Date",
      sort: true,
      formatter: (cell, row) => <b>{row.date}</b>,
    },
    {
      dataField: "case_desc",
      text: "Details",
      sort: true,
    },
  ]

  useEffect(() => {
    // Load Clients
    dispatch(clientOptions({ type: "billto" }))
  }, [])

  const getOrdersByNumber = async orderNumber => {
    if (orderNumber) {
      setOrderLoading(true)
      const res = await get(`${ORDERBYNUM}/${orderNumber}`)

      if (res.status == 200) {
        setSelectedOrder(res.response)
        setOrderLoading(false)
      } else {
        setSelectedOrder(null)
        setOrderLoading(false)
      }
    } else {
      setSelectedOrder(null)
      setOrderLoading(false)
    }
  }

  const previewCardStyles = `
    .preview-card-body-wrapper {
      font-family: Arial, sans-serif;
      background-color: #f0f0f0;
      padding: 20px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    .print-guide-banner {
        width: 850px;
        background-color: #2b231d;
        border-left: 5px solid #c9a054;
        color: #ffffff;
        padding: 15px 20px;
        margin-bottom: 25px;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
    .print-guide-banner h3 {
        margin: 0 0 8px 0;
        font-size: 14px;
        letter-spacing: 0.5px;
        color: #f4e3b1;
        text-transform: uppercase;
    }
    .print-guide-banner ul {
        margin: 0;
        padding-left: 20px;
        font-size: 11.5px;
        line-height: 1.5;
        color: #e5d4bc;
    }
    .warranty-card-horizontal {
        display: flex;
        flex-direction: row;
        width: 850px;
        background-color: #ffffff;
        border: 1px solid #ccc;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        box-sizing: border-box;
        margin-bottom: 20px;
        text-align: left;
    }
    .column-section {
        flex: 1;
        width: 50%;
        padding: 12px 18px;
        border-right: 1px dotted #999;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
    }
    .column-section:last-child {
        border-right: none;
    }
    .terms-column {
        font-size: 8.5px;
        line-height: 1.35;
        color: #333333;
        text-align: justify;
    }
    .terms-heading {
        font-weight: bold;
        font-size: 10px;
        margin-bottom: 5px;
    }
    .terms-paragraph {
        margin-bottom: 6px;
    }
    .terms-footer-container {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        margin-top: auto;
        padding-top: 4px;
    }
    .terms-footer {
        font-style: italic;
        max-width: 65%;
    }
    .seal-box {
        border: 1px dashed #777;
        width: 110px;
        height: 42px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 9.5px;
        font-weight: bold;
        color: #666;
        background-color: rgba(255, 255, 255, 0.7);
        box-sizing: border-box;
    }
    .form-column {
        gap: 8px;
        position: relative;
        justify-content: space-between;
    }
    .form-column::before {
        content: "DG";
        position: absolute;
        top: 45%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 90px;
        font-weight: bold;
        color: rgba(0, 80, 160, 0.02);
        font-family: 'Arial Black', sans-serif;
        z-index: 1;
        pointer-events: none;
    }
    .brand-header {
        z-index: 2;
    }
    .brand-title {
        font-weight: bold;
        font-size: 13px;
        color: #000000;
        letter-spacing: 0.5px;
    }
    .brand-subtitle {
        font-size: 10px;
        font-weight: bold;
        color: #333333;
        margin-top: 1px;
    }
    .form-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        row-gap: 8px;
        column-gap: 12px;
        font-size: 10.5px;
        font-weight: bold;
        color: #222222;
        z-index: 2;
    }
    .field-line {
        border-bottom: 1px dotted #000;
        display: inline-block;
        flex-grow: 1;
        margin-left: 4px;
        min-height: 14px;
        font-weight: bold;
        padding-left: 4px;
        word-break: break-word;
        max-width: 100%;
    }
    .field-wrapper {
        display: flex;
        align-items: flex-end;
    }
    .full-width-field {
        grid-column: span 2;
        display: flex;
        align-items: flex-end;
    }
    .form-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 10px;
        font-weight: bold;
        margin-top: auto;
        padding-top: 4px;
        z-index: 2;
    }
    .checkbox-container {
        display: flex;
        align-items: center;
        gap: 4px;
    }
    .checkbox-box {
        width: 11px;
        height: 11px;
        border: 1px solid #000;
        background-color: #fff;
    }
    .web-url {
        font-size: 9.5px;
        color: #000000;
        text-decoration: none;
    }
  `;

  const printStyles = `
    @media print {
      body {
        background: #ffffff !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      #layout-wrapper {
        display: none !important;
      }
      .modal-backdrop {
        display: none !important;
      }
      #warranty-card-print-area {
        position: absolute !important;
        left: 0 !important;
        top: 0 !important;
        width: 850px !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .modal-header, .modal-footer, .print-guide-banner, .modal-close {
        display: none !important;
      }
      .modal {
        position: absolute !important;
        left: 0 !important;
        top: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        border: none !important;
        background: transparent !important;
        box-shadow: none !important;
        display: block !important;
        width: 100% !important;
        height: auto !important;
        overflow: visible !important;
      }
      .modal-dialog {
        max-width: 100% !important;
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .modal-content {
        border: none !important;
        background: transparent !important;
        box-shadow: none !important;
        padding: 0 !important;
      }
      .modal-body {
        padding: 0 !important;
      }
    }
  `;

  return (
    <React.Fragment>
      <style dangerouslySetInnerHTML={{ __html: `.table-responsive tbody tr { cursor: pointer; }` }} />
      <div className="page-content">
        <MetaTags>
          {" "}
          <title>{isType} | Oralstop</title>{" "}
        </MetaTags>
        <Container fluid>
          <Row>
            <Col md={4}>
              <Card>
                <CardBody>
                  <Row>
                    <Col>
                      <label>Order Number or Modal Number</label>
                      <input
                        className="form-control"
                        onChange={e =>
                          debouncedGetOrdersByNumber(e.target.value)
                        }
                        placeholder="Order Number or Modal Number"
                      />
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col>
                      <div className="order-summary-card" style={{ position: "relative", minHeight: "150px" }}>
                        {orderLoading ? (
                          <div className="text-center py-5">
                            <i className="bx bx-loader bx-spin font-size-24 text-primary" />
                            <p className="mt-2 text-muted">Loading order details...</p>
                          </div>
                        ) : (
                          <table className="table table-borderless">
                            <tbody>
                              <tr className="border-bottom">
                                <th className="text-muted">Client Name</th>
                                <td className="fw-semibold text-dark">
                                  {selectedOrder ? selectedOrder.clientname : "-"}
                                </td>
                              </tr>
                              <tr className="border-bottom">
                                <th className="text-muted">Patient</th>
                                <td className="fw-semibold text-dark">
                                  {selectedOrder ? selectedOrder.patient_name : "-"}
                                </td>
                              </tr>
                              <tr className="border-bottom">
                                <th className="text-muted">Order Value</th>
                                <td className="fw-semibold text-success">
                                  {selectedOrder ? parseFloat(selectedOrder.order_value).toFixed(2) : "-"}
                                </td>
                              </tr>
                              <tr>
                                <th colSpan={2} className="text-muted pt-2">
                                  Products
                                </th>
                              </tr>
                              <tr>
                                <td colSpan={2}>
                                  {selectedOrder?.products && selectedOrder.products.length > 0 ? (
                                    selectedOrder.products.map((p, i) => (
                                      <div key={i} className="mb-2 d-flex justify-content-between align-items-center border-bottom pb-2">
                                        <button
                                          type="button"
                                          className="btn btn-link text-primary p-0 text-start animate-hover"
                                          onClick={() => handleProductClick(p)}
                                          style={{ textDecoration: 'none', fontWeight: 'bold' }}
                                        >
                                          {p.product_title}
                                        </button>
                                        <span className="text-muted font-size-12">
                                          ({p.unit} units)
                                        </span>
                                      </div>
                                    ))
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col md={8}>
              <div className="cardfilters">
                <Row>
                  <Col md={6} className="mb-3">
                    <label>Select Client</label>
                    <Select
                      styles={{
                        menu: provided => ({ ...provided, zIndex: 9999 }),
                      }}
                      onChange={e => {
                        setClientId(e)
                      }}
                      isClearable={true}
                      value={clientId}
                      options={DDOptions?.clients}
                      name={"docs"}
                      required={false}
                      placeholder={"Select Doctor"}
                    />
                  </Col>
                  <Col md={6} className="mb-3">
                    <label>Search Warranty Cards</label>
                    <input
                      className="form-control"
                      onChange={e => debouncedSearch(e.target.value)}
                      placeholder="Search by Warranty Code, Order No, Patient, Client..."
                    />
                  </Col>
                </Row>
              </div>

              <Card>
                <CardBody>
                  <Datatable
                    keyField="id"
                    title={`All ${isType}`}
                    columns={tableColumns}
                    rowsLength={challans?.total_rows || 0}
                    rows={challans?.rows || []}
                    isAdd={false}
                    isTableHead={false}
                    isPaginationProvider={false}
                    loading={listLoading && (!batchProgress || batchProgress.loaded === 0)}
                    batchProgress={batchProgress}
                    onCancelBatch={handleCancelBatch}
                    ssr={handlePaginationChange}
                    rowEvents={{
                      onClick: (e, row, rowIndex) => {
                        handlePreviewClick(row)
                      }
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)} centered={true}>
            <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)}>
              Generate Warranty Card
            </ModalHeader>
            <ModalBody>
              <FormGroup className="mb-3">
                <label className="form-label">Product</label>
                <input
                  type="text"
                  className="form-control"
                  value={activeProduct ? activeProduct.product_title : ""}
                  readOnly
                />
              </FormGroup>
              <FormGroup className="mb-3">
                <label className="form-label">Warranty Years</label>
                {activeProduct?.warranty_years ? (
                  <input
                    type="text"
                    className="form-control"
                    value={warrantyYears}
                    readOnly
                  />
                ) : (
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="100"
                    value={warrantyYears}
                    onChange={(e) => setWarrantyYears(e.target.value)}
                    placeholder="Enter warranty years"
                  />
                )}
              </FormGroup>
              <FormGroup className="mb-3">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </FormGroup>
              <FormGroup className="mb-3">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={calculateEndDate(startDate, warrantyYears)}
                  readOnly
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button color="primary" onClick={handleSaveWarranty}>
                Save Warranty Card
              </Button>
            </ModalFooter>
          </Modal>

          <Modal isOpen={isPreviewOpen} toggle={() => setIsPreviewOpen(!isPreviewOpen)} size="lg" style={{ maxWidth: '900px' }} centered={true}>
            <style dangerouslySetInnerHTML={{ __html: previewCardStyles + printStyles }} />
            <ModalHeader toggle={() => setIsPreviewOpen(!isPreviewOpen)}>
              Warranty Card Preview
            </ModalHeader>
            <ModalBody className="preview-card-body-wrapper">
              {previewCardData && (
                <>
                  <div className="print-guide-banner">
                    <h3>📐 Production Scale Settings</h3>
                    <ul>
                      <li><strong>Actual Dimensions:</strong> Target output footprint maps to <strong>850px width</strong> matching premium wide landscape display metrics.</li>
                      <li><strong>Print Configurations:</strong> Set layout alignment to <strong>Landscape</strong> and keep resolution scaling exactly at <strong>100% / Actual Size</strong>.</li>
                    </ul>
                  </div>

                  <div id="warranty-card-print-area">
                    <div className="warranty-card-horizontal">
                      {/* COLUMN 1: Terms and Conditions + Integrated Seal */}
                      <div className="column-section terms-column">
                        <div className="terms-heading">Limited Warranty and Limitation of Remedy and Liability:</div>

                        <div className="terms-paragraph">
                          {"We make no other warranties including but not limited to any implied warranty of merchantability for fitness for a particular purpose. Our sole obligation and the customer's sole remedy in the event of any breakage of the frame work shall be limited to replacement of the sintered frame work restoration."}
                        </div>

                        <div className="terms-paragraph">
                          This warranty does not cover breakage resulting from accident or misuse nor any additional cause such as
                          but not limited to the cost of preparations of veneering and neither will be liable for any loss or
                          damages arising from This product whether it is direct, indirect, special, incidental or consequential
                          regardless of the theory asserted including warrant, contract, negligent or strict liability.
                        </div>

                        <div className="terms-footer-container">
                          <div className="terms-footer">
                            This warranty card should be produced for any claim.
                          </div>
                          <div className="seal-box">
                            Lab Seal
                          </div>
                        </div>
                      </div>

                      {/* COLUMN 2: Meta Form Sheets Registry */}
                      <div className="column-section form-column">
                        <div className="brand-header">
                          <div className="brand-title">DG DIGITAL DENTAL LAB</div>
                          <div className="brand-subtitle">Limited Warranty*</div>
                        </div>

                        <div className="form-grid">
                          <div className="field-wrapper" style={{ maxWidth: '170px' }}>
                            <span>Date:</span>
                            <span className="field-line">{previewCardData.date}</span>
                          </div>
                          <div className="field-wrapper">
                            <span>Case Number:</span>
                            <span className="field-line">{previewCardData.case_number}</span>
                          </div>
                          <div className="full-width-field">
                            <span>Dentist Name:</span>
                            <span className="field-line">{previewCardData.clientname}</span>
                          </div>
                          <div className="full-width-field">
                            <span>Patient Name:</span>
                            <span className="field-line">{previewCardData.patiantname}</span>
                          </div>
                          <div className="full-width-field" style={{ gap: '15px' }}>
                            <div className="field-wrapper" style={{ flexGrow: 1, display: 'flex', alignItems: 'flex-end' }}>
                              <span>Product Details:</span>
                              <span className="field-line">{previewCardData.product_title || previewCardData.product}</span>
                            </div>
                            <div className="field-wrapper" style={{ width: '130px', display: 'flex', alignItems: 'flex-end' }}>
                              <span>Tooth No:</span>
                              <span className="field-line">{parseTeeth(previewCardData.teeth)}</span>
                            </div>
                          </div>
                          <div className="full-width-field">
                            <span>Dentist Signature:</span>
                            <span className="field-line"></span>
                          </div>
                        </div>

                        <div className="form-footer">
                          <div className="checkbox-container">
                            <span>Full Contour</span>
                            <div className="checkbox-box"></div>
                          </div>
                          <a href="http://www.dgddlab.com" className="web-url" target="_blank" rel="noreferrer">www.dgddlab.com</a>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setIsPreviewOpen(false)}>
                Close
              </Button>
              <Button color="primary" onClick={() => window.print()}>
                Print Warranty Card
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default WarrantyCard
